import {
  HttpClient,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { APP_ENV } from '../data-access/app-config';
import { getBaseOrigin } from './subdomain-config';

const REFRESH_ENDPOINT = '/api/v1/Token/exchangeRefreshToken';
const JWT_PARTS_COUNT = 3;
const BASE64_BLOCK_SIZE = 4;

function getCustomerCodeFromToken(token: string | null): string | null {
  if (!token) {
    return null;
  }
  try {
    const parts = token.split('.');
    if (parts.length !== JWT_PARTS_COUNT) {
      return null;
    }
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(
      payload.length +
        ((BASE64_BLOCK_SIZE - (payload.length % BASE64_BLOCK_SIZE)) %
          BASE64_BLOCK_SIZE),
      '=',
    );
    const decoded = JSON.parse(atob(padded)) as { customer_code?: string };
    return decoded.customer_code ?? null;
  } catch {
    return null;
  }
}

function handleLogout(): void {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // Ignore storage cleanup failures so redirect can still happen.
  }
  window.location.href = `${getBaseOrigin()}/login`;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const http = inject(HttpClient);
  const appEnv = inject(APP_ENV);
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const customerId = getCustomerCodeFromToken(accessToken);
  if (customerId) {
    localStorage.setItem('customerCode', customerId);
  }

  const languageCode = localStorage.getItem('languageCode') ?? 'en';

  const modifiedReq = req.clone({
    setHeaders: {
      ...(customerId
        ? { 'x-customer-id': customerId, 'IRU-Customer-ID': customerId }
        : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'x-language-code': languageCode,
    },
  });

  return next(modifiedReq).pipe(
    catchError((err: HttpErrorResponse) => {
      const isRefreshRequest = req.url.includes('Token/exchangeRefreshToken');
      const isUnauthorized = err.status === +HttpStatusCode.Unauthorized;

      if (!isRefreshRequest && isUnauthorized) {
        if (!accessToken || !refreshToken) {
          handleLogout();
          return throwError(() => err);
        }

        return http
          .post<{ accessToken?: string; refreshToken?: string }>(
            `${appEnv.idpBaseUrl}${REFRESH_ENDPOINT}`,
            {
              expiredToken: accessToken,
              refreshToken,
            },
          )
          .pipe(
            switchMap((res) => {
              if (res.accessToken) {
                localStorage.setItem('accessToken', res.accessToken);
              }
              if (res.refreshToken) {
                localStorage.setItem('refreshToken', res.refreshToken);
              }
              const newAccessToken = res.accessToken ?? accessToken;
              const retryCustomerId =
                getCustomerCodeFromToken(newAccessToken) ?? customerId;
              if (retryCustomerId) {
                localStorage.setItem('customerCode', retryCustomerId);
              }
              const retryReq = req.clone({
                setHeaders: {
                  ...(retryCustomerId
                    ? {
                        'x-customer-id': retryCustomerId,
                        'IRU-Customer-ID': retryCustomerId,
                      }
                    : {}),
                  Authorization: `Bearer ${newAccessToken}`,
                  'x-language-code': languageCode,
                },
              });
              return next(retryReq);
            }),
            catchError(() => {
              handleLogout();
              return throwError(() => err);
            }),
          );
      }
      return throwError(() => err);
    }),
  );
};
